import type { NewsItem } from '../types/news.types'
import { NewsCard } from './NewsCard'
import { Pagination } from './Pagination'
import { Inbox, Search, Loader2 } from 'lucide-react'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// NewsGrid Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface NewsGridProps {
  items: NewsItem[]
  isLoading?: boolean
  error?: string | null
  keyword?: string
  total?: number
  currentPage?: number
  itemsPerPage?: number
  onPageChange?: (page: number) => void
}

export function NewsGrid({
  items,
  isLoading,
  error,
  keyword,
  total = 0,
  currentPage = 1,
  itemsPerPage = 10,
  onPageChange,
}: NewsGridProps) {
  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="text-muted-foreground">뉴스를 불러오는 중...</p>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
        <div className="rounded-full bg-destructive/10 p-4">
          <Search className="h-12 w-12 text-destructive" />
        </div>
        <div className="text-center">
          <p className="mb-2 font-semibold text-destructive">검색 중 오류가 발생했습니다</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    )
  }

  // No results
  if (!items || items.length === 0) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
        <div className="rounded-full bg-muted p-4">
          <Inbox className="h-12 w-12 text-muted-foreground" />
        </div>
        <div className="text-center">
          <p className="mb-2 font-semibold">검색 결과가 없습니다</p>
          {keyword && (
            <p className="text-sm text-muted-foreground">
              &quot;{keyword}&quot;에 대한 뉴스를 찾을 수 없습니다
            </p>
          )}
        </div>
      </div>
    )
  }

  // News grid
  return (
    <div className="space-y-6">
      {/* Pagination */}
      {total > 0 && onPageChange && (
        <Pagination
          currentPage={currentPage}
          totalItems={total}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      )}

      {/*
        Container queries, so the grid responds to its own width rather than the
        viewport's. The main area only gets `viewport - sidebar`, and the sidebar is
        user-resizable between 280 and 600px, so viewport breakpoints were guesswork:
        they could hand a card ~145px and explode a Korean headline onto seven lines.
      */}
      <div className="@container">
        <div className="grid gap-4 @md:grid-cols-2 @3xl:grid-cols-3 @5xl:grid-cols-4">
          {items.map((item, index) => (
            <NewsCard key={`${item.link}-${index}`} item={item} keyword={keyword} />
          ))}
        </div>
      </div>
    </div>
  )
}
